"use client";

import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { LoginButton } from "@/components/auth/login-button";
import { EmailLoginForm } from "@/components/auth/email-login-form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { FileText, AlertCircle, Mail, CheckCircle2 } from "lucide-react";

function LoginContent() {
  const searchParams = useSearchParams();
  const error = searchParams.get("error");
  const callbackError = searchParams.get("callbackError");
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const t = useTranslations();

  const getErrorMessage = () => {
    if (callbackError) return callbackError;
    switch (error) {
      case "OAuthCallback":
        return t("login.errorOAuthCallback");
      case "OAuthAccountNotLinked":
        return t("login.errorAccountNotLinked");
      case "SessionExpired":
        return t("login.errorSessionExpired");
      case "AccessDenied":
        return t("login.errorAccessDenied");
      default:
        return error ? t("login.errorGeneric") : null;
    }
  };

  const errorMessage = getErrorMessage();

  return (
    <div className="flex min-h-screen">
      {/* Left panel - gradient with features */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[oklch(0.15_0.05_270)] to-[oklch(0.20_0.08_290)] text-foreground overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "radial-gradient(circle, oklch(0.65 0.22 270 / 0.4) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <div className="inline-flex items-center gap-3 mb-8">
            <div className="p-3 bg-primary/15 backdrop-blur glow-sm rounded-xl">
              <FileText className="h-10 w-10" />
            </div>
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight mb-4">
            {t("landing.heading")}
          </h1>
          <p className="text-lg text-muted-foreground mb-10">
            {t("landing.subtitle")}
          </p>
          <div className="space-y-5">
            {[
              t("landing.feature1"),
              t("landing.feature2"),
              t("landing.feature3"),
            ].map((text) => (
              <div key={text} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 text-primary/80 shrink-0" />
                <span className="text-foreground/90">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel - login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-background relative">
        {/* Language switcher in top-right corner */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher variant="text" />
        </div>
        <div className="w-full max-w-sm">
          <div className="flex items-center mb-8">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/billance-ledger-logo.png"
              alt="Billance Ledger"
              className="h-14 w-auto"
            />
          </div>

          <h2 className="text-2xl font-bold mb-1">{t("login.title")}</h2>
          <p className="text-muted-foreground mb-8">{t("login.subtitle")}</p>

          {errorMessage && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {showEmailLogin ? (
            <EmailLoginForm onBack={() => setShowEmailLogin(false)} />
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <LoginButton />
              </div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">{t("login.or")}</span>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowEmailLogin(true)}
              >
                <Mail className="mr-2 h-4 w-4" /> {t("login.loginWithEmail")}
              </Button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t text-center text-sm text-muted-foreground space-y-2">
            <p>{t("login.bookkeeperOnly")}</p>
            <p>
              {t("login.areYouClient")}{" "}
              <Link href="/client/login" className="text-primary hover:underline font-medium">
                {t("login.goToClientPortal")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">…</p>
      </div>
    }>
      <LoginContent />
    </Suspense>
  );
}
